/**
 * Verify SMS Actor
 *
 * Verifies OTP code and authenticates user.
 */

import type { OTPData } from "@frontend/lib/interfaces";
import { fromPromise } from "xstate";
import { z } from "zod";
import { submitOTP } from "../../server-actions/otp-actions";

/**
 * Input Schema
 */
const VerifySMSInputSchema = z.object({
  userId: z.string(),
  otp: z.string(),
  otpTime: z.string(),
});

type VerifySMSInput = z.infer<typeof VerifySMSInputSchema>;

/**
 * Success Schema
 */
const VerifySMSSuccessSchema = z.object({
  verified: z.literal(true),
});

type VerifySMSSuccess = z.infer<typeof VerifySMSSuccessSchema>;

/**
 * Error Schema
 */
const VerifySMSErrorSchema = z.object({
  code: z.enum(["INVALID_INPUT", "OTP_INVALID", "NETWORK_ERROR"]),
  message: z.string(),
  retryable: z.boolean(),
});

type VerifySMSError = z.infer<typeof VerifySMSErrorSchema>;

/**
 * Dependencies
 */
export interface VerifySMSDependencies {
  analytics?: {
    track: (event: string, properties?: Record<string, unknown>) => void;
  };
}

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Verify SMS Actor Implementation
 *
 * NOTE: XState compatibility - we check ActionResult.success:
 * - throw error → XState onError transition
 * - return value → XState onDone transition
 */
const verifySMSActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: VerifySMSInput & { deps: VerifySMSDependencies };
  }): Promise<VerifySMSSuccess> => {
    const { userId, otp, otpTime, deps } = input;

    // Validate input
    const validated = VerifySMSInputSchema.safeParse({
      userId,
      otp,
      otpTime,
    });

    if (!validated.success) {
      const errorData: VerifySMSError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error; // XState onError
    }

    // Prepare OTP data for submission
    const otpData: OTPData = {
      userId,
      OTP: otp,
      time: otpTime,
    };

    // Submit OTP
    const result = await submitOTP(otpData);

    if (!result.success) {
      const errorData: VerifySMSError = {
        code: result.error.code as VerifySMSError["code"],
        message: result.error.message,
        retryable: result.error.retryable,
      };
      console.log("[verifySMSActor] OTP verification failed:", errorData);
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error; // XState onError
    }

    // Track analytics
    deps?.analytics?.track("otp_verified", {
      userId,
    });

    // Tokens are already set as cookies by submitOTP
    // No need to return them to context
    const success: VerifySMSSuccess = {
      verified: true,
    };
    return success; // XState onDone
  },
);

/**
 * Verify SMS Actor with Metadata
 */
export const verifySMSActor: ActorWithMetadata = {
  impl: verifySMSActorImpl,
  metadata: {
    displayName: "verifySMS",
    description: "Verify OTP code and authenticate user",
    category: "auth",
    retryable: true,
    timeout: 10000, // 10 seconds
    errorCodes: ["INVALID_INPUT", "OTP_INVALID", "NETWORK_ERROR"],
    inputSchema: VerifySMSInputSchema,
    successSchema: VerifySMSSuccessSchema,
    errorSchema: VerifySMSErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as VerifySMSSuccess;
      return {
        verified: success.verified,
        smsAuthenticated: true, // Set SMS authentication flag
        otpError: undefined,
      };
    },
    onError: (error: unknown) => {
      const err = error as VerifySMSError;
      return {
        otpError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
